import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Loader2, Pencil, Check, X } from "lucide-react";

export default function AdminBanners() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadTitle, setUploadTitle] = useState("");
    const [uploadSubtitle, setUploadSubtitle] = useState("");
    const [editing, setEditing] = useState(null); // { id, title, subtitle }
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/banners");
            setBanners(data);
        } catch (e) { toast.error("Falha ao carregar banners"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleUpload = async (file) => {
        if (!file) return;
        if (!uploadTitle.trim()) {
            toast.error("Informe um título antes de enviar");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: up } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const publicUrl = up.url.startsWith("http") ? up.url : `${process.env.REACT_APP_BACKEND_URL}${up.url}`;
            await api.post("/admin/banners", {
                title: uploadTitle.trim(),
                subtitle: uploadSubtitle.trim() || null,
                image_url: publicUrl,
                is_active: true,
            });
            toast.success("Banner publicado");
            setUploadTitle("");
            setUploadSubtitle("");
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no upload");
        } finally { setUploading(false); }
    };

    const toggleActive = async (b) => {
        try {
            await api.patch(`/admin/banners/${b.id}`, { is_active: !b.is_active });
            setBanners((prev) => prev.map((x) => x.id === b.id ? { ...x, is_active: !b.is_active } : x));
        } catch (e) { toast.error("Falha ao alternar"); }
    };

    const remove = async (b) => {
        if (!window.confirm(`Excluir banner "${b.title}"?`)) return;
        try {
            await api.delete(`/admin/banners/${b.id}`);
            setBanners((prev) => prev.filter((x) => x.id !== b.id));
            toast.success("Excluído");
        } catch (e) { toast.error("Falha ao excluir"); }
    };

    const move = async (index, dir) => {
        const newIndex = index + dir;
        if (newIndex < 0 || newIndex >= banners.length) return;
        const arr = [...banners];
        [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
        setBanners(arr);
        try {
            await api.post("/admin/banners/reorder", { ids: arr.map((b) => b.id) });
        } catch (e) {
            toast.error("Falha ao reordenar");
            await load();
        }
    };

    const startEdit = (b) => setEditing({ id: b.id, title: b.title, subtitle: b.subtitle || "" });
    const saveEdit = async () => {
        try {
            await api.patch(`/admin/banners/${editing.id}`, {
                title: editing.title.trim(),
                subtitle: editing.subtitle.trim() || null,
            });
            setBanners((prev) => prev.map((x) => x.id === editing.id ? { ...x, title: editing.title.trim(), subtitle: editing.subtitle.trim() || null } : x));
            setEditing(null);
            toast.success("Atualizado");
        } catch (e) { toast.error("Falha ao salvar"); }
    };

    return (
        <div className="p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Landing · Carrossel</div>
                    <h1 className="mt-1 font-heading font-black text-3xl tracking-tight">Banners</h1>
                    <p className="mt-2 text-sm text-neutral-500">{banners.length} banner(s) · exibidos no carrossel público entre marcas parceiras e catálogo em destaque.</p>
                </div>
            </div>

            {/* UPLOAD CARD */}
            <div className="mt-8 bg-white border-2 border-brand-blue/20 p-6" data-testid="banner-upload-card">
                <div className="text-xs font-mono uppercase tracking-widest text-brand-blue mb-3">// novo banner</div>
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                    <div>
                        <label className="text-xs font-mono uppercase tracking-widest text-neutral-500">Título *</label>
                        <input
                            data-testid="banner-title-input"
                            value={uploadTitle}
                            onChange={(e) => setUploadTitle(e.target.value)}
                            placeholder="Ex: EPIs — Proteção que garante"
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 focus:outline-none focus:border-brand-blue"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-mono uppercase tracking-widest text-neutral-500">Subtítulo (opcional)</label>
                        <input
                            data-testid="banner-subtitle-input"
                            value={uploadSubtitle}
                            onChange={(e) => setUploadSubtitle(e.target.value)}
                            placeholder="Ex: Cabeça, auditiva, ocular, respiratória…"
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 focus:outline-none focus:border-brand-blue"
                        />
                    </div>
                </div>
                <label className="block cursor-pointer">
                    <input
                        ref={fileRef}
                        data-testid="banner-file-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => handleUpload(e.target.files?.[0])}
                        className="hidden"
                        disabled={uploading}
                    />
                    <div className={`border-2 border-dashed border-neutral-300 hover:border-brand-blue bg-neutral-50 p-8 text-center transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                        {uploading ? (
                            <div className="flex items-center justify-center gap-2 text-brand-blue font-heading font-bold">
                                <Loader2 size={16} className="animate-spin" /> Enviando…
                            </div>
                        ) : (
                            <>
                                <Upload size={24} className="mx-auto text-neutral-400 mb-2" />
                                <div className="text-sm font-heading font-bold">Clique para selecionar imagem</div>
                                <div className="text-xs text-neutral-500 mt-1 font-mono">JPG · PNG · WEBP até 8 MB · recomendado 1830×850px (proporção 21:9)</div>
                            </>
                        )}
                    </div>
                </label>
            </div>

            {/* LIST */}
            {loading ? (
                <div className="mt-8 font-mono text-sm text-neutral-500">carregando…</div>
            ) : banners.length === 0 ? (
                <div className="mt-8 border border-dashed border-neutral-300 bg-white p-16 text-center">
                    <div className="font-mono text-xs uppercase tracking-widest text-neutral-400">// nenhum banner</div>
                    <p className="mt-3 text-neutral-600">Envie o primeiro banner acima. O carrossel só aparece na landing quando há pelo menos 1 banner ativo.</p>
                </div>
            ) : (
                <div className="mt-8 grid gap-4">
                    {banners.map((b, idx) => (
                        <div key={b.id} data-testid={`banner-row-${b.id}`} className={`bg-white border border-neutral-300 grid grid-cols-[220px,1fr,auto] gap-5 p-4 ${!b.is_active ? "opacity-60" : ""}`}>
                            <div className="aspect-[21/9] bg-neutral-100 border border-neutral-200 overflow-hidden">
                                <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            </div>
                            <div>
                                {editing?.id === b.id ? (
                                    <div className="space-y-2">
                                        <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full h-10 px-3 border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                                        <input value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} placeholder="Subtítulo" className="w-full h-10 px-3 border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                                        <div className="flex gap-2">
                                            <button onClick={saveEdit} className="h-9 px-3 bg-brand-blue text-white text-sm font-heading font-bold uppercase tracking-wider flex items-center gap-1"><Check size={14} /> Salvar</button>
                                            <button onClick={() => setEditing(null)} className="h-9 px-3 border border-neutral-300 text-sm flex items-center gap-1"><X size={14} /> Cancelar</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">ordem {String(b.order).padStart(2, "0")} · {b.is_active ? <span className="text-green-700">ativo</span> : <span className="text-red-600">inativo</span>}</div>
                                        <div className="mt-1 font-heading font-bold text-lg leading-snug">{b.title}</div>
                                        {b.subtitle && <div className="mt-1 text-sm text-neutral-600">{b.subtitle}</div>}
                                        <div className="mt-2 text-[10px] font-mono text-neutral-400 truncate">{b.image_url}</div>
                                    </>
                                )}
                            </div>
                            <div className="flex flex-col items-end gap-1.5">
                                <div className="flex gap-1">
                                    <button data-testid={`banner-up-${b.id}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="h-9 w-9 border border-neutral-300 disabled:opacity-40 hover:bg-neutral-50 grid place-items-center"><ChevronUp size={14} /></button>
                                    <button data-testid={`banner-down-${b.id}`} disabled={idx === banners.length - 1} onClick={() => move(idx, 1)} className="h-9 w-9 border border-neutral-300 disabled:opacity-40 hover:bg-neutral-50 grid place-items-center"><ChevronDown size={14} /></button>
                                </div>
                                <button data-testid={`banner-toggle-${b.id}`} onClick={() => toggleActive(b)} className="h-9 px-3 border border-neutral-300 hover:bg-neutral-50 text-sm flex items-center gap-1.5">
                                    {b.is_active ? <><Eye size={13} /> Ativo</> : <><EyeOff size={13} /> Oculto</>}
                                </button>
                                <button data-testid={`banner-edit-${b.id}`} onClick={() => startEdit(b)} className="h-9 px-3 border border-neutral-300 hover:bg-neutral-50 text-sm flex items-center gap-1.5"><Pencil size={13} /> Editar</button>
                                <button data-testid={`banner-delete-${b.id}`} onClick={() => remove(b)} className="h-9 px-3 border border-neutral-300 hover:bg-red-50 hover:border-red-300 text-red-700 text-sm flex items-center gap-1.5"><Trash2 size={13} /> Excluir</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
